import CreationToolOutputType from './output-type'
import CreationToolUserRestriction from './user-restriction'

/**
 * Fields available on the upload screen.
 */
enum CreationToolUploadField {
    SourceUrl = 'CT_UPLOAD_FIELD_SOURCE_URL',
    Tags = 'CT_UPLOAD_FIELD_TAGS',
    Privacy = 'CT_UPLOAD_FIELD_PRIVACY',
    SuggestedTags = 'CT_UPLOAD_FIELD_SUGGESTED_TAGS',
    Rating = 'CT_UPLOAD_FIELD_RATING',
    VideoRating = 'CT_UPLOAD_FIELD_VIDEO_RATING',
    VideoTitle = 'CT_UPLOAD_FIELD_VIDEO_TITLE',
    VideoDescription = 'CT_UPLOAD_FIELD_VIDEO_DESCRIPTION',
    Username = 'CT_UPLOAD_FIELD_USERNAME',
    Collections = 'CT_UPLOAD_FIELD_COLLECTIONS',
    CtaToggle = 'CT_UPLOAD_FIELD_CTA_TOGGLE',
    CtaText = 'CT_UPLOAD_FIELD_CTA_TEXT',
}

namespace CreationToolUploadField {
    /**
     * Returns all of the upload fields in order.
     */
    export function all(): CreationToolUploadField[] {
        return [
            CreationToolUploadField.Privacy,
            CreationToolUploadField.VideoDescription,
            CreationToolUploadField.Tags,
            CreationToolUploadField.SuggestedTags,
            CreationToolUploadField.SourceUrl,
            CreationToolUploadField.CtaText,
            CreationToolUploadField.Rating,
            CreationToolUploadField.VideoRating,
            CreationToolUploadField.Username,
            CreationToolUploadField.Collections,
        ]
    }

    /**
     * Returns the upload field's title.
     */
    export function title(field: CreationToolUploadField): string {
        switch (field) {
            case CreationToolUploadField.SourceUrl:
                return 'Source URL'
            case CreationToolUploadField.CtaText:
                return 'Button Title'
            case CreationToolUploadField.CtaToggle:
                return 'CTA Button'
            case CreationToolUploadField.Tags:
                return 'Add Tags'
            case CreationToolUploadField.Privacy:
                return 'Visibility'
            case CreationToolUploadField.SuggestedTags:
                return 'Suggested Tags'
            case CreationToolUploadField.Rating:
            case CreationToolUploadField.VideoRating:
                return 'Rating'
            case CreationToolUploadField.Username:
                return 'Username'
            case CreationToolUploadField.Collections:
                return 'Add to Collection'
            case CreationToolUploadField.VideoTitle:
                return 'Clip Title'
            case CreationToolUploadField.VideoDescription:
                return 'Clip Description'
        }
    }

    /**
     * Returns the upload field's brief description.
     */
    export function description(field: CreationToolUploadField): string {
        switch (field) {
            case CreationToolUploadField.SourceUrl:
                return 'Add a source URL'
            case CreationToolUploadField.Tags:
                return 'Add tags'
            case CreationToolUploadField.Username:
                return 'Add a username'
            case CreationToolUploadField.VideoTitle:
                return 'Add a title to your Clip'
            case CreationToolUploadField.VideoDescription:
                return 'Add a description to your Clip'
            case CreationToolUploadField.CtaText:
                return 'Add a button title'
            default:
                return ''
        }
    }

    /**
     * Returns the upload field's instruction text.
     */
    export function instructions(field: CreationToolUploadField): string {
        switch (field) {
            case CreationToolUploadField.Tags:
                return `Tags help find your content later. Separate with commas to add multiple tags.`
            default:
                return ''
        }
    }

    /**
     * Returns the upload field's learn more url.
     */
    export function learnMoreUrl(field: CreationToolUploadField): string {
        switch (field) {
            case CreationToolUploadField.Privacy:
                return 'https://support.giphy.com/hc/en-us/articles/360020233051-Get-Your-GIFs-Stickers-Into-GIPHY-s-Search-'
            default:
                return ''
        }
    }

    /**
     * Returns the ouput file type restrictions for the field, if it has them.
     */
    export function ouputTypeRestrictions(field: CreationToolUploadField): CreationToolOutputType[] {
        switch (field) {
            case CreationToolUploadField.VideoRating:
            case CreationToolUploadField.VideoTitle:
            case CreationToolUploadField.VideoDescription:
            case CreationToolUploadField.CtaText:
                return [CreationToolOutputType.Video]
            case CreationToolUploadField.Rating:
                return CreationToolOutputType.all().filter(type => type !== CreationToolOutputType.Video)
            default:
                return CreationToolOutputType.all()
        }
    }

    /**
     * Returns a user restriction for the field, if it has one.
     */
    export function userRestriction(field: CreationToolUploadField): CreationToolUserRestriction {
        switch (field) {
            case CreationToolUploadField.Rating:
                return CreationToolUserRestriction.StaffOnly
            case CreationToolUploadField.VideoRating:
                return CreationToolUserRestriction.CanRateVideo
            case CreationToolUploadField.VideoTitle:
            case CreationToolUploadField.VideoDescription:
                return CreationToolUserRestriction.CanUploadVideo
            case CreationToolUploadField.Username:
                return CreationToolUserRestriction.StaffOnly
            case CreationToolUploadField.SuggestedTags:
                return CreationToolUserRestriction.CanViewSuggestedTags
            case CreationToolUploadField.CtaText:
                return CreationToolUserRestriction.VerifiedOnly
            default:
                return CreationToolUserRestriction.Everyone
        }
    }

    /**
     * Returns the max character count for the field, if it has one.
     */
    export function maxCharacterCount(field: CreationToolUploadField): number {
        switch (field) {
            case CreationToolUploadField.VideoTitle:
                return 50
            case CreationToolUploadField.VideoDescription:
                return 200
            case CreationToolUploadField.CtaText:
                return 32
            default:
                return 0
        }
    }
}

export default CreationToolUploadField
